/**
 * Normalize an optimize-campaign run into the digest the optimization agent
 * reasons over.
 *
 * The digest is built in the FRONTEND for the same reason the bulk editor's row
 * digest is (see services/bulkEdit/toolExecutor.js's header): the optimizer
 * emits a different suggestion shape per platform, the presentation layer
 * already knows how to read all of them, and an agent that addressed a
 * differently-derived set of suggestions than the ones on screen would be worse
 * than no agent. So the surface normalizes once and the agent edits exactly what
 * the user is looking at.
 *
 * Two run shapes, both handled:
 *   Google Ads / LinkedIn — details.analysis is an OBJECT carrying
 *     `actionable_suggestions`, `campaign_analysis` and `key_insights`.
 *   MadHive / Reddit / Facebook — details.analysis is a STRING and the
 *     suggestions live in `details.actionable_items`.
 *
 * `index` is the suggestion's position in the run's own array and is what
 * `POST /campaigns/:id/edit` records as `suggestionIndex`. It is the stable id
 * across the agent, the preview, the apply call and the "already applied" check,
 * so it must stay tied to the source array's order — never to a filtered or
 * sorted view of it.
 */

import { buildEditDescription } from '../../../utils/optimizerSuggestionApply';
import { resolveOptimizationSuggestions, resolveOptimizationAnalysisText } from '../../../utils/optimizationSuggestions';

const asText = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'object') {
    if (Array.isArray(v)) {
      return v
        .map((x) => (x && typeof x === 'object' ? (x.keyword || JSON.stringify(x)) : String(x)))
        .join(', ');
    }
    return JSON.stringify(v);
  }
  return String(v);
};

/**
 * The raw suggestion array for a run, whichever shape it came in.
 *
 * Delegates to utils/optimizationSuggestions — the SAME resolver the run
 * cards use. This function used to know two keys
 * (`analysis.actionable_suggestions`, `actionable_items`) and therefore
 * digested DV360 and TTD runs (`pacing_recommendations` / `recommendations`)
 * to ZERO suggestions. An empty digest means useOptimizationSession never
 * opens a session at all, so the Optimization tab sat there with no summary
 * and no error — demo 0820, "optimization summary all failed". The card
 * reader had already been fixed; this copy was left behind, which is exactly
 * why there is now one resolver instead of three.
 */
export function extractSuggestions(details) {
  return resolveOptimizationSuggestions(details);
}

/**
 * A human label for one suggestion — the same header OptimizeResultBubble
 * renders, so the agent's wording and the card's wording agree.
 */
export function suggestionLabel(s) {
  if (s.target_type && s.target_id) return `${s.target_type} ${s.target_id}`;
  if (s.entity_type && s.entity_name) return `${s.entity_type} ${s.entity_name}`;
  if (s.entity_name) return String(s.entity_name);
  if (s.target_id) return String(s.target_id);
  return 'campaign';
}

/** action_type as a field-ish label for the diff row. */
export function suggestionField(s) {
  if (s.action_type) return String(s.action_type).replace(/_/g, ' ');
  if (s.type) return String(s.type).replace(/_/g, ' ');
  return 'change';
}

/**
 * @param {object} runResult a message json_response from an optimize-campaign run
 * @param {object} opts { appliedIndexes?: Set|Array } indexes already applied
 * @returns {object} digest for the agent + the card
 */
export function buildOptimizerDigest(runResult, { appliedIndexes = [] } = {}) {
  const details = runResult?.details || null;
  const analysis = details?.analysis;
  const applied = appliedIndexes instanceof Set ? appliedIndexes : new Set(appliedIndexes || []);

  const suggestions = extractSuggestions(details).map((s, index) => {
    const description = buildEditDescription(s);
    return {
      index,
      ...(s.analysisKind ? { analysisKind: s.analysisKind, sourceMessageId: s.sourceMessageId, sourceSuggestionIndex: s.sourceSuggestionIndex } : {}),
      label: suggestionLabel(s),
      field: suggestionField(s),
      actionType: s.action_type || s.type || null,
      entityType: s.target_type || s.entity_type || null,
      entityId: s.target_id || s.entity_name || null,
      before: asText(s.current_value),
      after: asText(s.recommended_value ?? s.suggested_values),
      priority: s.priority ? String(s.priority).toLowerCase() : null,
      rationale: s.rationale || s.reasoning || s.description || null,
      // An un-describable suggestion cannot be turned into an edit request, so
      // it is reported to the agent as not applicable rather than being offered
      // and then failing at apply time.
      // DV360 pacing items carry action_type/target_type/target_id, so a
      // description CAN be built for them — but their action vocabulary is not
      // the apply path's, so they are advisory only. Without this the agent
      // would offer to apply prose at a live campaign, re-opening the hole the
      // card reader deliberately closed.
      applicable: Boolean(description) && !s.advisory,
      alreadyApplied: applied.has(index),
      // The description the apply call will actually send. Surfaced so the agent
      // can quote what will happen rather than paraphrasing it.
      editDescription: description,
    };
  });

  const insights = (analysis && typeof analysis === 'object' && Array.isArray(analysis.campaign_analysis?.key_insights))
    ? analysis.campaign_analysis.key_insights
    : [];

  return {
    platformCampaignId: details?.campaign_id || runResult?.campaign_id || null,
    // Same resolver as the cards, so DV360/TTD prose (campaign_analysis +
    // pacing_summary + optimization_summary, all top-level on details) is not
    // silently dropped. `analysis.summary` is a fallback and is stringified:
    // on the real Google Ads artifact it is an OBJECT (next_steps, …), and an
    // object here ships into session_data and read_optimization_run as one.
    // Order matters: Google Ads' own `analysis.summary` stays FIRST (that is
    // the working behaviour), then the shared resolver picks up the DV360/TTD
    // prose that lives at the TOP level of details (campaign_analysis,
    // pacing_summary, optimization_summary) and was previously dropped whole.
    // asText because on the real gads artifact `analysis.summary` is an
    // OBJECT (next_steps, …) — an object here shipped straight into
    // session_data and read_optimization_run.
    summary: (typeof analysis === 'string' && analysis.trim())
      ? analysis
      : (asText(analysis?.summary)
        || resolveOptimizationAnalysisText(details)
        || analysis?.campaign_analysis?.overall_performance
        || null),
    insights: insights.map((i) => ({
      insight: i.insight || asText(i),
      severity: i.severity ? String(i.severity).toUpperCase() : null,
    })),
    keyMetrics: (analysis && typeof analysis === 'object')
      ? (analysis.campaign_analysis?.key_metrics || analysis.campaign_analysis?.performance_metrics || null)
      : null,
    suggestions,
    // Counts the agent would otherwise have to derive by scanning, and which its
    // opening summary is judged on.
    counts: {
      total: suggestions.length,
      applicable: suggestions.filter((s) => s.applicable && !s.alreadyApplied).length,
      alreadyApplied: suggestions.filter((s) => s.alreadyApplied).length,
      byPriority: suggestions.reduce((acc, s) => {
        const k = s.priority || 'unspecified';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {}),
      byAction: suggestions.reduce((acc, s) => {
        const k = s.actionType || 'unspecified';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {}),
    },
  };
}

export default { buildOptimizerDigest, extractSuggestions, suggestionLabel, suggestionField };
